// Centralized Enterprise Data Constants for Clouds on Mars
// Based on global healthcare/medical device company structure

import DataConsistencyService from '../services/DataConsistencyService';

export const COMPANY_INFO = {
  name: 'Clouds on Mars',
  domain: 'cloudonmars.com',
  industry: 'Healthcare Technology & Medical Devices',
  founded: 1996,
  headquarters: 'Bad Homburg, Germany',
  employees: DataConsistencyService.getTotalEmployees(), // Global workforce
  countries: 190, // Countries with presence
  facilities: 4000, // Global facilities
  revenue: '20.5B EUR', // Annual revenue
  tenantId: 'cloudonmars.onmicrosoft.com'
};

// Global locations matching real healthcare company presence
export const GLOBAL_LOCATIONS = [
  // Europe (Headquarters Region)
  { id: 'de-bad-homburg', country: 'Germany', city: 'Bad Homburg', region: 'Europe', isHQ: true, employees: 8500, currency: 'EUR' },
  { id: 'de-schweinfurt', country: 'Germany', city: 'Schweinfurt', region: 'Europe', isHQ: false, employees: 6200, currency: 'EUR' },
  { id: 'de-oberursel', country: 'Germany', city: 'Oberursel', region: 'Europe', isHQ: false, employees: 3800, currency: 'EUR' },
  { id: 'es-barcelona', country: 'Spain', city: 'Barcelona', region: 'Europe', isHQ: false, employees: 2100, currency: 'EUR' },
  { id: 'it-milan', country: 'Italy', city: 'Milan', region: 'Europe', isHQ: false, employees: 1800, currency: 'EUR' },
  { id: 'fr-paris', country: 'France', city: 'Paris', region: 'Europe', isHQ: false, employees: 1600, currency: 'EUR' },
  { id: 'uk-london', country: 'United Kingdom', city: 'London', region: 'Europe', isHQ: false, employees: 1400, currency: 'GBP' },
  { id: 'ch-zurich', country: 'Switzerland', city: 'Zurich', region: 'Europe', isHQ: false, employees: 950, currency: 'CHF' },
  { id: 'at-vienna', country: 'Austria', city: 'Vienna', region: 'Europe', isHQ: false, employees: 800, currency: 'EUR' },
  { id: 'pl-warsaw', country: 'Poland', city: 'Warsaw', region: 'Europe', isHQ: false, employees: 1200, currency: 'PLN' },
  
  // North America
  { id: 'us-waltham', country: 'United States', city: 'Waltham, MA', region: 'North America', isHQ: false, employees: 15500, currency: 'USD' },
  { id: 'us-ogden', country: 'United States', city: 'Ogden, UT', region: 'North America', isHQ: false, employees: 4200, currency: 'USD' },
  { id: 'us-richmond', country: 'United States', city: 'Richmond, CA', region: 'North America', isHQ: false, employees: 3800, currency: 'USD' },
  { id: 'us-memphis', country: 'United States', city: 'Memphis, TN', region: 'North America', isHQ: false, employees: 2900, currency: 'USD' },
  { id: 'ca-toronto', country: 'Canada', city: 'Toronto', region: 'North America', isHQ: false, employees: 1800, currency: 'CAD' },
  
  // Asia-Pacific
  { id: 'jp-tokyo', country: 'Japan', city: 'Tokyo', region: 'Asia-Pacific', isHQ: false, employees: 6500, currency: 'JPY' },
  { id: 'cn-beijing', country: 'China', city: 'Beijing', region: 'Asia-Pacific', isHQ: false, employees: 8200, currency: 'CNY' },
  { id: 'cn-shanghai', country: 'China', city: 'Shanghai', region: 'Asia-Pacific', isHQ: false, employees: 5400, currency: 'CNY' },
  { id: 'au-melbourne', country: 'Australia', city: 'Melbourne', region: 'Asia-Pacific', isHQ: false, employees: 1200, currency: 'AUD' },
  { id: 'sg-singapore', country: 'Singapore', city: 'Singapore', region: 'Asia-Pacific', isHQ: false, employees: 2100, currency: 'SGD' },
  { id: 'kr-seoul', country: 'South Korea', city: 'Seoul', region: 'Asia-Pacific', isHQ: false, employees: 1800, currency: 'KRW' },
  { id: 'in-bangalore', country: 'India', city: 'Bangalore', region: 'Asia-Pacific', isHQ: false, employees: 4500, currency: 'INR' },
  
  // Latin America
  { id: 'br-sao-paulo', country: 'Brazil', city: 'São Paulo', region: 'Latin America', isHQ: false, employees: 3200, currency: 'BRL' },
  { id: 'mx-mexico-city', country: 'Mexico', city: 'Mexico City', region: 'Latin America', isHQ: false, employees: 2100, currency: 'MXN' },
  { id: 'ar-buenos-aires', country: 'Argentina', city: 'Buenos Aires', region: 'Latin America', isHQ: false, employees: 1400, currency: 'ARS' },
  
  // Middle East & Africa
  { id: 'ae-dubai', country: 'United Arab Emirates', city: 'Dubai', region: 'Middle East & Africa', isHQ: false, employees: 1100, currency: 'AED' },
  { id: 'za-johannesburg', country: 'South Africa', city: 'Johannesburg', region: 'Middle East & Africa', isHQ: false, employees: 900, currency: 'ZAR' },
  { id: 'tr-istanbul', country: 'Turkey', city: 'Istanbul', region: 'Middle East & Africa', isHQ: false, employees: 1300, currency: 'TRY' }
];

// Business units aligned with healthcare/medical device industry
export const BUSINESS_UNITS = [
  {
    id: 'care-coordination',
    name: 'Care Coordination',
    description: 'Digital health platforms and care management solutions',
    employees: 18500,
    revenue: '4.2B EUR'
  },
  {
    id: 'care-enablement',
    name: 'Care Enablement', 
    description: 'Medical devices and equipment for healthcare providers',
    employees: 22800,
    revenue: '5.8B EUR'
  },
  {
    id: 'care-delivery',
    name: 'Care Delivery',
    description: 'Direct patient care services and clinical operations',
    employees: 78200,
    revenue: '8.9B EUR'
  },
  {
    id: 'global-manufacturing',
    name: 'Global Manufacturing',
    description: 'Manufacturing operations and supply chain',
    employees: 8900,
    revenue: '1.1B EUR'
  },
  {
    id: 'corporate-functions',
    name: 'Corporate Functions',
    description: 'IT, Finance, HR, Legal, and other support functions',
    employees: 3600,
    revenue: '0.5B EUR'
  }
];

// Departments within business units
export const DEPARTMENTS = [
  // Care Coordination
  { id: 'digital-health', name: 'Digital Health Solutions', businessUnit: 'care-coordination', employees: 4200 },
  { id: 'data-analytics', name: 'Data Analytics & AI', businessUnit: 'care-coordination', employees: 2800 },
  { id: 'software-development', name: 'Software Development', businessUnit: 'care-coordination', employees: 6500 },
  { id: 'product-management', name: 'Product Management', businessUnit: 'care-coordination', employees: 1800 },
  { id: 'customer-solutions', name: 'Customer Solutions', businessUnit: 'care-coordination', employees: 3200 },
  
  // Care Enablement
  { id: 'medical-devices', name: 'Medical Device Engineering', businessUnit: 'care-enablement', employees: 8900 },
  { id: 'clinical-affairs', name: 'Clinical Affairs & Research', businessUnit: 'care-enablement', employees: 3400 },
  { id: 'regulatory-affairs', name: 'Regulatory Affairs', businessUnit: 'care-enablement', employees: 1900 },
  { id: 'quality-assurance', name: 'Quality Assurance', businessUnit: 'care-enablement', employees: 2800 },
  { id: 'technical-support', name: 'Technical Support', businessUnit: 'care-enablement', employees: 5800 },
  
  // Care Delivery
  { id: 'clinical-operations', name: 'Clinical Operations', businessUnit: 'care-delivery', employees: 62500 },
  { id: 'nursing', name: 'Nursing', businessUnit: 'care-delivery', employees: 9800 },
  { id: 'patient-services', name: 'Patient Services', businessUnit: 'care-delivery', employees: 4200 },
  { id: 'facility-management', name: 'Facility Management', businessUnit: 'care-delivery', employees: 1700 },
  
  // Global Manufacturing
  { id: 'production', name: 'Production', businessUnit: 'global-manufacturing', employees: 5200 },
  { id: 'supply-chain', name: 'Supply Chain', businessUnit: 'global-manufacturing', employees: 1800 },
  { id: 'procurement', name: 'Procurement', businessUnit: 'global-manufacturing', employees: 900 },
  { id: 'logistics', name: 'Logistics', businessUnit: 'global-manufacturing', employees: 1000 },
  
  // Corporate Functions
  { id: 'human-resources', name: 'Human Resources', businessUnit: 'corporate-functions', employees: 800 },
  { id: 'information-technology', name: 'Information Technology', businessUnit: 'corporate-functions', employees: 1200 },
  { id: 'finance', name: 'Finance', businessUnit: 'corporate-functions', employees: 600 },
  { id: 'legal', name: 'Legal & Compliance', businessUnit: 'corporate-functions', employees: 400 },
  { id: 'corporate-development', name: 'Corporate Development', businessUnit: 'corporate-functions', employees: 300 },
  { id: 'communications', name: 'Communications & Marketing', businessUnit: 'corporate-functions', employees: 300 }
];

// Job levels and career progression
export const JOB_LEVELS = [
  { id: 'L1', name: 'Associate', grade: 'IC1', minSalary: 35000, maxSalary: 55000, employees: 12400 },
  { id: 'L2', name: 'Specialist', grade: 'IC2', minSalary: 50000, maxSalary: 75000, employees: 18600 },
  { id: 'L3', name: 'Senior Specialist', grade: 'IC3', minSalary: 70000, maxSalary: 95000, employees: 24800 },
  { id: 'L4', name: 'Principal', grade: 'IC4', minSalary: 90000, maxSalary: 125000, employees: 16200 },
  { id: 'L5', name: 'Senior Principal', grade: 'IC5', minSalary: 120000, maxSalary: 160000, employees: 8900 },
  { id: 'L6', name: 'Distinguished', grade: 'IC6', minSalary: 155000, maxSalary: 210000, employees: 1800 },
  
  // Management Track
  { id: 'M1', name: 'Team Lead', grade: 'M1', minSalary: 85000, maxSalary: 115000, employees: 5600 },
  { id: 'M2', name: 'Manager', grade: 'M2', minSalary: 110000, maxSalary: 145000, employees: 8200 },
  { id: 'M3', name: 'Senior Manager', grade: 'M3', minSalary: 140000, maxSalary: 180000, employees: 4100 },
  { id: 'M4', name: 'Director', grade: 'M4', minSalary: 175000, maxSalary: 225000, employees: 2400 },
  { id: 'M5', name: 'Senior Director', grade: 'M5', minSalary: 220000, maxSalary: 280000, employees: 1200 },
  { id: 'M6', name: 'Vice President', grade: 'M6', minSalary: 275000, maxSalary: 350000, employees: 380 },
  { id: 'M7', name: 'Senior Vice President', grade: 'M7', minSalary: 345000, maxSalary: 450000, employees: 120 },
  { id: 'M8', name: 'Executive Vice President', grade: 'M8', minSalary: 445000, maxSalary: 600000, employees: 35 },
  { id: 'M9', name: 'C-Level', grade: 'C', minSalary: 595000, maxSalary: 1200000, employees: 12 }
];

// Currency information and conversion rates (base: EUR)
export const CURRENCIES = {
  EUR: { symbol: '€', name: 'Euro', rate: 1.0, format: 'de-DE' },
  USD: { symbol: '$', name: 'US Dollar', rate: 1.08, format: 'en-US' },
  GBP: { symbol: '£', name: 'British Pound', rate: 0.86, format: 'en-GB' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc', rate: 0.98, format: 'de-CH' },
  JPY: { symbol: '¥', name: 'Japanese Yen', rate: 161.5, format: 'ja-JP' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', rate: 7.75, format: 'zh-CN' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', rate: 1.46, format: 'en-CA' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', rate: 1.65, format: 'en-AU' },
  BRL: { symbol: 'R$', name: 'Brazilian Real', rate: 5.95, format: 'pt-BR' },
  PLN: { symbol: 'zł', name: 'Polish Zloty', rate: 4.32, format: 'pl-PL' },
  INR: { symbol: '₹', name: 'Indian Rupee', rate: 90.2, format: 'en-IN' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', rate: 1.44, format: 'en-SG' },
  KRW: { symbol: '₩', name: 'South Korean Won', rate: 1445, format: 'ko-KR' },
  MXN: { symbol: '$', name: 'Mexican Peso', rate: 18.8, format: 'es-MX' },
  ARS: { symbol: '$', name: 'Argentine Peso', rate: 365, format: 'es-AR' },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', rate: 3.96, format: 'ar-AE' },
  ZAR: { symbol: 'R', name: 'South African Rand', rate: 19.8, format: 'en-ZA' },
  TRY: { symbol: '₺', name: 'Turkish Lira', rate: 31.2, format: 'tr-TR' }
};

// Employee tenure distribution
export const TENURE_BANDS = [
  { id: 'under-1', name: '< 1 year', employees: 15800, percentage: 12 },
  { id: '1-2', name: '1-2 years', employees: 19800, percentage: 15 },
  { id: '3-5', name: '3-5 years', employees: 31700, percentage: 24 },
  { id: '6-10', name: '6-10 years', employees: 35900, percentage: 27 },
  { id: '11-15', name: '11-15 years', employees: 18500, percentage: 14 },
  { id: '16-20', name: '16-20 years', employees: 7900, percentage: 6 },
  { id: 'over-20', name: '20+ years', employees: 2400, percentage: 2 }
];

// Age demographics
export const AGE_BANDS = [
  { id: 'under-25', name: '< 25', employees: 8900, percentage: 6.7 },
  { id: '25-34', name: '25-34', employees: 39600, percentage: 30.0 },
  { id: '35-44', name: '35-44', employees: 42200, percentage: 32.0 },
  { id: '45-54', name: '45-54', employees: 29000, percentage: 22.0 },
  { id: '55-64', name: '55-64', employees: 11600, percentage: 8.8 },
  { id: 'over-65', name: '65+', employees: 700, percentage: 0.5 }
];

// Performance ratings distribution
export const PERFORMANCE_DISTRIBUTION = [
  { rating: 'Exceptional', percentage: 8, count: 10560 },
  { rating: 'Exceeds Expectations', percentage: 22, count: 29040 },
  { rating: 'Meets Expectations', percentage: 60, count: 79200 },
  { rating: 'Below Expectations', percentage: 8, count: 10560 },
  { rating: 'Needs Improvement', percentage: 2, count: 2640 }
];

// Diversity metrics
export const DIVERSITY_METRICS = {
  gender: {
    female: { percentage: 47.2, count: 62304 },
    male: { percentage: 51.8, count: 68376 },
    nonBinary: { percentage: 0.8, count: 1056 },
    notSpecified: { percentage: 0.2, count: 264 }
  },
  leadership: {
    femaleInLeadership: { percentage: 42.1, count: 2100 }, // M4 and above
    diverseInLeadership: { percentage: 38.5, count: 1925 }
  }
};

// Skills categories for healthcare/medical device industry
export const SKILL_CATEGORIES = [
  'Clinical Research', 'Medical Device Development', 'Regulatory Affairs', 'Quality Management',
  'Data Science', 'Artificial Intelligence', 'Cloud Computing', 'Software Development',
  'Project Management', 'Clinical Operations', 'Biomedical Engineering', 'Healthcare Analytics',
  'Compliance Management', 'Risk Management', 'Digital Health', 'Patient Safety',
  'Supply Chain Management', 'Manufacturing', 'Process Improvement', 'Leadership'
];

// Global presence statistics
export const GLOBAL_PRESENCE = {
  regions: 5,
  countries: 190,
  majorOffices: 27,
  manufacturingFacilities: 45,
  clinics: 4000,
  researchCenters: 12
};

// Helper functions
export const getLocationById = (id: string) => 
  GLOBAL_LOCATIONS.find(loc => loc.id === id);

export const getLocationsByRegion = (region: string) =>
  GLOBAL_LOCATIONS.filter(loc => loc.region === region);

export const getLocationsByCountry = (country: string) =>
  GLOBAL_LOCATIONS.filter(loc => loc.country === country);

export const getDepartmentsByBusinessUnit = (businessUnitId: string) =>
  DEPARTMENTS.filter(dept => dept.businessUnit === businessUnitId);

export const formatCurrency = (amount: number, currencyCode: string) => {
  const currency = CURRENCIES[currencyCode as keyof typeof CURRENCIES];
  if (!currency) return `${amount}`;
  
  return new Intl.NumberFormat(currency.format, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export const convertCurrency = (amount: number, fromCurrency: string, toCurrency: string) => {
  const from = CURRENCIES[fromCurrency as keyof typeof CURRENCIES];
  const to = CURRENCIES[toCurrency as keyof typeof CURRENCIES];
  
  if (!from || !to) return amount;
  
  // Convert to EUR first, then to target currency
  const eurAmount = amount / from.rate;
  return eurAmount * to.rate;
};

export const getJobLevelBySalary = (salary: number, currency: string = 'EUR') => {
  const eurSalary = convertCurrency(salary, currency, 'EUR');
  return JOB_LEVELS.find(level => 
    eurSalary >= level.minSalary && eurSalary <= level.maxSalary
  );
};

export const getTotalEmployeesByRegion = () => {
  const regionTotals: { [key: string]: number } = {};
  GLOBAL_LOCATIONS.forEach(location => {
    if (!regionTotals[location.region]) {
      regionTotals[location.region] = 0;
    }
    regionTotals[location.region] += location.employees;
  });
  return regionTotals;
};